import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import SideBar from "../components/SideBar";
import itPortalService from "../services/itPortalService";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import { MdEdit, MdDelete, MdRefresh } from "react-icons/md";
import "../styles/ManageMember.css";
import "../styles/ManageUserTopCoin.css";

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

export default function ManageTCIndustry() {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const navigate = useNavigate();

  // Notification state
  const [snack, setSnack] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Modal states
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  // Form states
  const [formData, setFormData] = useState({
    TCIndustry: "",
    Active: true,
  });

  useEffect(() => {
    fetchRecords();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const showSnack = (message, severity = "success") => {
    setSnack({ open: true, message, severity });
  };

  const handleSnackClose = (event, reason) => {
    if (reason === "clickaway") return;
    setSnack((prev) => ({ ...prev, open: false }));
  };

  const fetchRecords = async () => {
    setLoading(true);
    try {
      const response = await itPortalService.getTCIndustries();
      const list = Array.isArray(response?.data?.data)
        ? response.data.data
        : Array.isArray(response?.data)
        ? response.data
        : Array.isArray(response)
        ? response
        : [];
      setRecords(list);
    } catch (err) {
      console.error("Failed to load TC Industries:", err);
      if (err.response?.status === 403 && err.response?.data?.requires_it_portal_otp) {
        navigate("/it-portal");
      } else {
        showSnack(
          err.response?.data?.message || err.message || "Failed to load TC Industries.",
          "error"
        );
      }
    } finally {
      setLoading(false);
    }
  };

  // Open Edit Modal
  const handleOpenEdit = (record) => {
    setSelectedRecord(record);
    setFormData({
      TCIndustry: record.TCIndustry,
      Active: Number(record.Active) === 1,
    });
    setIsEditModalOpen(true);
  };

  // Open Add Modal
  const handleOpenAdd = () => {
    setFormData({
      TCIndustry: "",
      Active: true,
    });
    setIsAddModalOpen(true);
  };

  // Save Edit
  const handleSaveEdit = async (e) => {
    e.preventDefault();
    if (!selectedRecord) return;

    setIsSaving(true);
    try {
      await itPortalService.updateTCIndustry(selectedRecord.ID, {
        TCIndustry: formData.TCIndustry.trim(),
        Active: formData.Active ? 1 : 0,
      });

      showSnack(`"${formData.TCIndustry}" updated successfully!`);
      setIsEditModalOpen(false);
      fetchRecords();
    } catch (err) {
      showSnack(
        err.response?.data?.message ||
          (err.response?.data?.errors
            ? Object.values(err.response.data.errors).flat().join(" ")
            : "Failed to update industry."),
        "error"
      );
    } finally {
      setIsSaving(false);
    }
  };

  // Save Add
  const handleSaveAdd = async (e) => {
    e.preventDefault();
    if (!formData.TCIndustry.trim()) {
      showSnack("Industry name is required.", "error");
      return;
    }

    setIsSaving(true);
    try {
      await itPortalService.createTCIndustry({
        TCIndustry: formData.TCIndustry.trim(),
        Active: formData.Active ? 1 : 0,
      });

      showSnack(`Industry "${formData.TCIndustry}" created successfully!`);
      setIsAddModalOpen(false);
      fetchRecords();
    } catch (err) {
      showSnack(
        err.response?.data?.message ||
          (err.response?.data?.errors
            ? Object.values(err.response.data.errors).flat().join(" ")
            : "Failed to create industry."),
        "error"
      );
    } finally {
      setIsSaving(false);
    }
  };

  // Confirm Delete
  const handleConfirmDelete = async () => {
    if (!selectedRecord) return;

    setIsSaving(true);
    try {
      await itPortalService.deleteTCIndustry(selectedRecord.ID);
      showSnack(`"${selectedRecord.TCIndustry}" removed successfully.`);
      setIsDeleteModalOpen(false);
      fetchRecords();
    } catch (err) {
      showSnack(
        err.response?.data?.message || "Failed to delete industry.",
        "error"
      );
    } finally {
      setIsSaving(false);
    }
  };

  // Filtered records
  const filteredRecords = records.filter((r) =>
    r.TCIndustry?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="manage-member-layout">
      <SideBar />

      <div className="main-content">
        <div className="header-bar" style={{ border: 0, boxShadow: "none" }}>
          <div className="header-top">
            <h2 className="header-title">TC Industry Management</h2>
            <div className="it-header-actions">
              {/* <button
                className="it-btn-refresh"
                onClick={fetchRecords}
                title="Refresh table data"
              >
                <MdRefresh size={18} /> Refresh
              </button> */}
              <button className="action-btn" onClick={handleOpenAdd}>
                + Add Industry
              </button>
            </div>
          </div>

          {/* Quick Statistics */}
          <div className="user-statistics">
            <div className="stat-item">
              <span className="stat-label">Total Industries:</span>
              <span className="stat-value">{records.length}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Active:</span>
              <span className="stat-value" style={{ color: "#166534" }}>
                {records.filter((r) => Number(r.Active) === 1).length}
              </span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Inactive:</span>
              <span className="stat-value" style={{ color: "#dc2626" }}>
                {records.filter((r) => Number(r.Active) === 0).length}
              </span>
            </div>
          </div>

          {/* Filter Bar */}
          <div className="filters">
            <div className="input-group" style={{ flexGrow: 1 }}>
              <label>Search Industry:</label>
              <input
                type="text"
                placeholder="Search by industry name..."
                className="search-input"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                maxLength={40}
              />
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="table-container">
          <table className="member-table">
            <thead>
              <tr>
                <th style={{ width: "70px" }}>ID</th>
                <th style={{ width: "280px" }}>Industry Name</th>
                <th style={{ width: "140px" }}>Status</th>
                <th style={{ width: "180px" }}>Created</th>
                <th style={{ width: "180px" }}>Updated</th>
                <th style={{ width: "130px", textAlign: "center" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="6" style={{ textAlign: "center", padding: "30px" }}>
                    Loading industries...
                  </td>
                </tr>
              ) : filteredRecords.length > 0 ? (
                filteredRecords.map((item) => (
                  <tr key={item.ID}>
                    <td style={{ fontWeight: 600 }}>{item.ID}</td>
                    <td>
                      <span
                        className="it-badge it-badge-retail"
                        style={{ fontWeight: 700 }}
                      >
                        {item.TCIndustry}
                      </span>
                    </td>
                    <td>
                      <span
                        style={{
                          display: "inline-block",
                          padding: "4px 12px",
                          borderRadius: "20px",
                          fontSize: "13px",
                          fontWeight: 600,
                          background:
                            Number(item.Active) === 1 ? "#dcfce7" : "#fee2e2",
                          color:
                            Number(item.Active) === 1 ? "#166534" : "#dc2626",
                          border: `1px solid ${
                            Number(item.Active) === 1 ? "#bbf7d0" : "#fecaca"
                          }`,
                        }}
                      >
                        {Number(item.Active) === 1 ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td style={{ fontSize: "13px", color: "#64748b" }}>
                      {item.created_at
                        ? new Date(item.created_at).toLocaleDateString("en-GB", {
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                          })
                        : "—"}
                    </td>
                    <td style={{ fontSize: "13px", color: "#64748b" }}>
                      {item.updated_at
                        ? new Date(item.updated_at).toLocaleDateString("en-GB", {
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                          })
                        : "—"}
                    </td>
                    <td style={{ textAlign: "center" }}>
                      <div className="it-table-actions">
                        <button
                          className="it-btn-edit"
                          onClick={() => handleOpenEdit(item)}
                          title="Edit Industry"
                        >
                          <MdEdit size={15} />
                        </button>
                        <button
                          className="it-btn-delete"
                          onClick={() => {
                            setSelectedRecord(item);
                            setIsDeleteModalOpen(true);
                          }}
                          title="Delete Industry"
                        >
                          <MdDelete size={15} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan="6"
                    style={{
                      textAlign: "center",
                      padding: "35px",
                      color: "#64748b",
                    }}
                  >
                    No industries found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Edit Modal */}
      {isEditModalOpen && selectedRecord && (
        <div className="it-modal-overlay">
          <div className="it-modal-box">
            <div className="it-modal-header">
              <h3>Edit Industry</h3>
              <button
                className="it-modal-close"
                onClick={() => !isSaving && setIsEditModalOpen(false)}
              >
                &times;
              </button>
            </div>
            <form onSubmit={handleSaveEdit}>
              <div className="it-modal-body">
                <div className="it-form-field">
                  <label>Industry Name *</label>
                  <input
                    type="text"
                    maxLength={150}
                    value={formData.TCIndustry}
                    onChange={(e) =>
                      setFormData({ ...formData, TCIndustry: e.target.value })
                    }
                    required
                    placeholder="e.g. RetailBusiness"
                  />
                </div>
                <div className="it-form-field">
                  <label>Status *</label>
                  <select
                    value={formData.Active ? "1" : "0"}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        Active: e.target.value === "1",
                      })
                    }
                  >
                    <option value="1">Active</option>
                    <option value="0">Inactive</option>
                  </select>
                  <small>
                    Active status determines if this industry is enabled for retail operations.
                  </small>
                </div>
              </div>
              <div className="it-modal-footer">
                <button
                  type="button"
                  className="it-btn-cancel"
                  onClick={() => setIsEditModalOpen(false)}
                  disabled={isSaving}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="action-btn"
                  style={{ marginTop: 0 }}
                  disabled={isSaving}
                >
                  {isSaving ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Modal */}
      {isAddModalOpen && (
        <div className="it-modal-overlay">
          <div className="it-modal-box">
            <div className="it-modal-header">
              <h3>Add New Industry</h3>
              <button
                className="it-modal-close"
                onClick={() => !isSaving && setIsAddModalOpen(false)}
              >
                &times;
              </button>
            </div>
            <form onSubmit={handleSaveAdd}>
              <div className="it-modal-body">
                <div className="it-form-field">
                  <label>Industry Name *</label>
                  <input
                    type="text"
                    maxLength={150}
                    value={formData.TCIndustry}
                    onChange={(e) =>
                      setFormData({ ...formData, TCIndustry: e.target.value })
                    }
                    required
                    placeholder="e.g. Hotel, AirMiles..."
                  />
                </div>
                <div className="it-form-field">
                  <label>Status *</label>
                  <select
                    value={formData.Active ? "1" : "0"}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        Active: e.target.value === "1",
                      })
                    }
                  >
                    <option value="1">Active</option>
                    <option value="0">Inactive</option>
                  </select>
                  <small>
                    Each industry automatically syncs with TopCoin Distribution Value management.
                  </small>
                </div>
              </div>
              <div className="it-modal-footer">
                <button
                  type="button"
                  className="it-btn-cancel"
                  onClick={() => setIsAddModalOpen(false)}
                  disabled={isSaving}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="action-btn"
                  style={{ marginTop: 0 }}
                  disabled={isSaving}
                >
                  {isSaving ? "Creating..." : "Create Industry"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && selectedRecord && (
        <div className="it-modal-overlay">
          <div className="it-modal-box" style={{ maxWidth: "450px" }}>
            <div className="it-modal-header" style={{ background: "#fef2f2" }}>
              <h3>Delete Industry</h3>
              <button
                className="it-modal-close"
                onClick={() => !isSaving && setIsDeleteModalOpen(false)}
              >
                &times;
              </button>
            </div>
            <div className="it-modal-body" style={{ textAlign: "center" }}>
              <p
                style={{
                  fontSize: "15px",
                  color: "#475569",
                  margin: "10px 0",
                }}
              >
                Are you sure you want to delete{" "}
                <strong>"{selectedRecord.TCIndustry}"</strong>?
              </p>
              <p
                style={{
                  fontSize: "13px",
                  color: "#dc2626",
                  margin: "8px 0 0 0",
                }}
              >
                This will also remove its associated TopCoin Distribution Value
                record.
              </p>
            </div>
            <div className="it-modal-footer">
              <button
                type="button"
                className="it-btn-cancel"
                onClick={() => setIsDeleteModalOpen(false)}
                disabled={isSaving}
              >
                Cancel
              </button>
              <button
                type="button"
                className="action-btn"
                style={{ marginTop: 0, background: "#dc2626" }}
                onClick={handleConfirmDelete}
                disabled={isSaving}
              >
                {isSaving ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Snackbar Alert */}
      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={handleSnackClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert onClose={handleSnackClose} severity={snack.severity}>
          {snack.message}
        </Alert>
      </Snackbar>
    </div>
  );
}
